import { Ops } from '@willie/schemas'
import type { StationType } from './constants'

export interface SiteDraft {
  /** Stable key, so a station can name a site that has no id yet. */
  key: string
  siteId: string | null
  siteName: string
  siteContactId: string | null
  installationContactId: string | null
}

export interface StationDraft {
  siteKey: string
  mode: 'create' | 'adopt'
  stationName: string
  stationType: StationType | ''
  internalId: string
  waterStationId: string
  pictureFromClientRequired: boolean
}

export interface BatchDraft {
  orgId: string
  kickoffDate: string
  plannedInstallationDate: string
  sites: SiteDraft[]
  stations: StationDraft[]
}

// A counter, not randomUUID: the key only has to be unique within one form, and randomUUID is
// absent outside a secure context — this dev server binds to the LAN, where the page is http.
let nextSiteKey = 0

export const emptySite = (): SiteDraft => ({
  key: `new-site-${nextSiteKey++}`,
  siteId: null,
  siteName: '',
  siteContactId: null,
  installationContactId: null
})

export const existingSite = (siteId: string, siteName: string): SiteDraft => ({
  key: siteId,
  siteId,
  siteName,
  siteContactId: null,
  installationContactId: null
})

export const emptyStation = (siteKey: string): StationDraft => ({
  siteKey,
  mode: 'create',
  stationName: '',
  stationType: '',
  internalId: '',
  waterStationId: '',
  pictureFromClientRequired: false
})

const toStationInput = (s: StationDraft): Ops.StationInstallations.BatchInstallation['station'] =>
  s.mode === 'adopt'
    ? { mode: 'adopt', waterStationId: s.waterStationId }
    : {
        mode: 'create',
        name: s.stationName.trim(),
        stationType: s.stationType as StationType,
        internalId: s.internalId.trim() || null
      }

export const toBatchInput = (
  values: BatchDraft,
  source: Ops.StationInstallations.BatchCreateInput['source']
): Ops.StationInstallations.BatchCreateInput => ({
  orgId: values.orgId,
  source,
  kickoffDate: values.kickoffDate || null,
  plannedInstallationDate: values.plannedInstallationDate || null,
  sites: values.sites.map((site) => ({
    siteId: site.siteId,
    siteName: site.siteName.trim(),
    siteContactId: site.siteContactId,
    installationContactId: site.installationContactId,
    installations: values.stations
      .filter((station) => station.siteKey === site.key)
      .map((station) => ({
        station: toStationInput(station),
        pictureFromClientRequired: station.pictureFromClientRequired
      }))
  }))
})

const isSiteNamed = (site: SiteDraft): boolean => site.siteId !== null || site.siteName.trim().length > 0

const needsContact = (site: SiteDraft): boolean => site.siteId === null && site.siteContactId === null

export const isSiteValid = (site: SiteDraft): boolean => isSiteNamed(site) && !needsContact(site)

export const sitesMissingContact = (sites: SiteDraft[]): string[] =>
  sites.filter((site) => isSiteNamed(site) && needsContact(site)).map((site) => site.siteName.trim())

const siteIdentity = (site: SiteDraft): string => site.siteId ?? `new:${Ops.StationInstallations.normalizeName(site.siteName)}`

/** The sites named more than once. Two of them would otherwise create two rows for one place. */
export const duplicateSiteNames = (sites: SiteDraft[]): string[] => {
  const counts = sites.reduce<Map<string, { name: string; count: number }>>((acc, site) => {
    if (!isSiteNamed(site)) return acc
    const key = siteIdentity(site)
    const entry = acc.get(key)
    return acc.set(key, { name: site.siteName.trim(), count: (entry?.count ?? 0) + 1 })
  }, new Map())
  return [...counts.values()].filter((entry) => entry.count > 1).map((entry) => entry.name)
}

export const isStationValid = (s: StationDraft): boolean =>
  s.mode === 'adopt' ? s.waterStationId !== '' : s.stationName.trim().length > 0 && s.stationType !== ''
